import { differenceInSeconds } from 'date-fns';

import { getTemporalProperty } from '../../components/swimlane/swimlane.utils';
import { commonRoutes } from '../../router/router.path';
import { getLogger } from '../../utils/logger/logger';
import { mapChannelToOriginChannel, mapToOriginChannel } from '../assetDetails/mappers';
import { AssetTypes } from '../root.types';

import { Channel, IngestedChannel, Program } from './channel.types';

const logger = getLogger('[ChannelMapper]');

export const mapToProgram = (program: RiksTV.ClientAPI.TitleWithCard): Program => {
  // Use extra check on season/episode since we've seen movies that has seriesName assigned...
  const card = program.card;
  const isTvSeries = program.seriesName != null && program.season != null && program.episode != null;

  // It seems that duration is often wrong on linear assets whereas broadcast(start/end) times give a correct value...
  const duration =
    program.titleType === 'Linear'
      ? differenceInSeconds(new Date(program.broadcastedTimeEnd), new Date(program.broadcastedTime))
      : program.duration;

  return {
    id: program.id,
    name: card.title,
    subtitle: getTemporalProperty(card.subtitles)?.value,
    description: card.description ?? '',
    image: card.image,
    time: program.broadcastedTime,
    duration: duration,
    playbackUrl: getLinkFromBehavior(program),
    coverPageUrl: getCoverpageUrl(program),
    isTvSeries: isTvSeries,
    seriesName: isTvSeries ? program.seriesName : undefined,
    tracking: card.tracking,
    state: getTemporalProperty(card.states)?.value,
    externalPlayback: getTemporalProperty(card.states)?.value !== 'Live' && !!program.card.contentInfo?.isExternal,
  };
};

export const mapToChannel = (
  channel: RiksTV.ClientAPI.ChannelResponse[0] | undefined,
  isLoggedIn: boolean
): Channel | undefined => {
  if (!channel) {
    return undefined;
  }

  const originChannel = channel?.programs?.[0]?.originChannel;
  const { archive, details, playDash, playDashPR, playHLSF, placeholderImage, epg } = channel._links;
  const model: IngestedChannel = {
    name: channel.serviceName,
    archiveUrl: archive.href,
    assetDetailsUrl: details.href || '',
    id: channel.channelId.toString(),
    isInSubscription: isLoggedIn ? channel.subscription : true,
    posterUrl: placeholderImage.href || originChannel?.logoUrl || '',
    programs: channel.programs?.map(mapToProgram) ?? [],
    streamUrls: {
      widevine: playDash.href,
      playready: playDashPR.href,
      fairplay: playHLSF.href,
    },
    epgUrl: epg.href,
    type: AssetTypes.CHANNEL,
    originChannel: channel.programs ? mapToOriginChannel(channel?.programs[0]) : mapChannelToOriginChannel(channel),
    isExternal: false,
    bufferLength: channel.ottinfo?.bufferLength,
  };
  // Map ExternalChannel fields
  if (channel.isExternal) {
    return {
      ...model,
      isExternal: true,
      tracking: channel.tracking!,
      links: {
        externalPlayback: channel._links.externalPlayback!,
      },
    };
  }
  return model;
};

export const channelMissingStreamsFilter = (channel: Channel) => {
  const { widevine, playready, fairplay } = channel.streamUrls;
  return widevine !== '' && playready !== '' && fairplay !== '';
};

const getLinkFromBehavior = (program: RiksTV.ClientAPI.TitleWithCard) => {
  const card = program.card;
  const target = getTemporalProperty(card.behaviors)?.target;
  const fallbackLink = commonRoutes.watchAsset({ id: program.id });
  switch (target) {
    case 'OpenCoverPage':
    case 'PlayAsset':
      return program.seriesId
        ? commonRoutes.watchTvSeries(program.seriesId)
        : commonRoutes.watchAsset({ id: program.id });
    case 'OpenEpisodeCoverPage': {
      if (card._links.episodeCoverPage?.id) {
        return commonRoutes.stackedSeriesOrMovie({
          id: card._links.episodeCoverPage.id,
          title: card.title,
          autoPlay: true,
          isSeries: program.seriesId != null,
        });
      }
      return fallbackLink;
    }
    case 'OpenSuperCoverPage': {
      if (card._links.superCoverPage) {
        return commonRoutes.stackedSeriesOrMovie({
          id: card._links.superCoverPage.id,
          title: card.title,
          isSeries: program.seriesId != null,
        });
      }
      return fallbackLink;
    }
    case 'OpenCollection': {
      if (card._links.collectionLink) {
        return commonRoutes.collections({ slug: card._links.collectionLink.slug });
      }
      return fallbackLink;
    }

    case 'PlayChannel':
    case 'PlayExternalChannel':
      return commonRoutes.watchLive(program.originChannel.channelId, program.originChannel.serviceName);
    case 'PlayExternalAsset':
    case undefined:
      return fallbackLink;
    default: {
      const _exhaustiveCheck: never = target;
      logger.warn('Unsupported behavior for link creation', target);
      return fallbackLink;
    }
  }
};

function getCoverpageUrl(program: RiksTV.ClientAPI.TitleWithCard): string | undefined {
  const card = program.card;
  if (card._links.episodeCoverPage?.id) {
    return commonRoutes.stackedSeriesOrMovie({
      id: card._links.episodeCoverPage.id,
      title: card.title,
      isSeries: program.seriesId != null,
    });
  }

  if (card._links.superCoverPage) {
    return commonRoutes.stackedSeriesOrMovie({
      id: card._links.superCoverPage.id,
      title: card.title,
      isSeries: program.seriesId != null,
    });
  }
  return undefined;
}
